<template>
  <Navbar />
  <!-- 主要內容區塊錨點 (搭配 Skip Link 與 Alt+C 快速鍵) -->
  <div id="main-content" tabindex="-1" class="main-content-anchor">
    <a
      id="accesskey-c"
      href="#accesskey-c"
      accesskey="C"
      class="accesskey-brick sr-only-focusable"
      title="主要內容區塊，快捷鍵：Alt+C"
      aria-label="主要內容區塊"
    >:::</a>
  </div>
  <div class="register">
    <RegProgressBar :currentStep="1" />

    <div class="register-form" v-if="!showProjectSites">
      <div class="register-form-title">
        <h2>{{ $t("register.title") }}</h2>
        <p class="subtitle">{{ $t("register.welcome") }}</p>
        <p class="description">
          目前共有 7 個計畫網站支援共通會員系統
          <span>(</span><a @click.prevent="showProjectSites = true"
            class="project-sites-link">查看共通會員計畫網站</a><span>)</span><br />
          完成註冊後，即可使用同一組帳號登入支援網站
        </p>
      </div>
      <div class="register-panel">
        <div class="login-email">
          <a-form :model="formModel" @finish="onFinish" autocomplete="off">
            <!-- Email -->
            <a-form-item class="custom-form-item custom-input" name="email" :rules="[]">
              <label for="email" class="form-label">
                {{ $t("register.email") }}<span class="required">*</span>
              </label>
              <a-input style="margin-top: 4px" id="email" name="email" autocomplete="off"
                :placeholder="$t('register.emailPlaceholder')" v-model:value="formModel.email" />
            </a-form-item>

            <!-- Password -->
            <a-form-item class="custom-form-item custom-input password-input" name="password" :rules="[]">
              <label for="password" class="form-label">
                {{ $t("register.password") }}<span class="required">*</span>
              </label>
              <a-input-password style="margin-top: 4px" id="password" name="password" autocomplete="new-password"
                :placeholder="$t('register.passwordPlaceholder')" v-model:value="formModel.password" />
              <div class="password-hint" v-if="formModel.password.length > 0">
                <CheckCircleFilled :style="{
                  color: isPasswordValid ? '#52c41a' : '#C0C0C0',
                  fontSize: '16px',
                }" />
                <span>{{ $t("register.passwordHint") }}</span>
              </div>
            </a-form-item>
          </a-form>
        </div>
        <div class="create-with-email">
          <a-button class="create-w-email-btn" :loading="loading" :disabled="!canRegister" @click="handleNextStep">
            <span>{{ $t("register.registerWithEmail") }}</span>
          </a-button>
          <div class="terms-agreement">
            <span class="terms-text">
              {{ $t("register.termsAgreement") }}
              <a @click="showPrivacyModal = true" class="terms-link">{{
                $t("register.privacyPolicy")
              }}</a>
            </span>
          </div>

          <div class="exist-account">
            <span class="toc-item">{{ $t("register.hasAccount") }}</span>
            <router-link to="/sign-in" class="toc-link">
              <span>{{ $t("register.login") }}</span>
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- 共通會員計畫網站 獨立列表視圖 -->
    <div class="register-form project-sites-card" v-else>
      <div class="project-sites-header">
        <div class="back-btn" @click="showProjectSites = false" title="返回註冊帳號">
          <LeftOutlined class="back-arrow-icon" />
        </div>
        <h2 class="project-sites-title">共通會員計畫網站</h2>
      </div>

      <div class="project-sites-list">
        <a v-for="(site, index) in projectSites" :key="index" :href="site.url" target="_blank" rel="noopener noreferrer"
          class="project-site-item">
          <div class="site-item-header">
            <span class="site-item-name">{{ site.name }}</span>
            <RightOutlined class="site-item-arrow" />
          </div>
          <p class="site-item-desc">{{ site.description }}</p>
        </a>
      </div>
    </div>
  </div>

  <!-- 隱私權政策 Modal -->
  <PrivacyPolicyModal v-model:open="showPrivacyModal" @agree="handlePrivacyAgree" @cancel="handlePrivacyCancel" />

  <!-- 使用者條款 Modal -->
  <TermsModal v-model:open="showTermsModal" @agree="handleTermsAgree" @cancel="handleTermsCancel" />

  <!-- 離開確認 Modal -->
  <LeaveConfirmationModal :open="showLeaveModal" @leave="handleLeaveConfirm" @stay="handleStay" />
</template>
<script setup>
import Navbar from "../components/Navbar.vue";
import PrivacyPolicyModal from "../components/PrivacyPolicyModal.vue";
import TermsModal from "../components/TermsModal.vue";
import LeaveConfirmationModal from "../components/LeaveConfirmationModal.vue";
import { onMounted, onUnmounted, ref, reactive, computed } from "vue";
import { CheckCircleFilled, LeftOutlined, RightOutlined } from "@ant-design/icons-vue";
import { message } from "ant-design-vue";
import { useRouter, onBeforeRouteLeave } from "vue-router";
import { useI18n } from "vue-i18n";
import RegProgressBar from "../components/RegProgressBar.vue";
import { authAPI } from "@/services/api.js";

const router = useRouter();
const { t: $t } = useI18n();
const formRef = ref(null);
const loading = ref(false);
const showProjectSites = ref(false);

const projectSites = [
  {
    name: "臺灣醫療資訊標準大平台",
    description: "串聯標準、資料、規則與應用，推動智慧醫療落地",
    url: "https://medstandard.mohw.gov.tw/",
  },
  {
    name: "臺灣健康資料空間",
    description: "實現病人跨院病歷互通自主",
    url: "https://thds.mohw.gov.tw/",
  },
  {
    name: "臺灣健康規則空間",
    description: "實現醫院健保公衛行政自動化",
    url: "https://thrs.mohw.gov.tw/",
  },
  {
    name: "臺灣健康應用空間",
    description: "實現所有智慧醫療科技",
    url: "https://thas.mohw.gov.tw/",
  },
  {
    name: "SNOMED CT",
    description: "建立一致的醫療術語語言",
    url: "https://twsnomedct.mohw.gov.tw/",
  },
  {
    name: "LOINC",
    description: "建立一致的檢驗資料語言",
    url: "https://twloinc.mohw.gov.tw/",
  },
  {
    name: "RxNorm",
    description: "建立一致的藥品資料語言",
    url: "https://twrxnorm.mohw.gov.tw/",
  },
];
const useEmail = computed(() => formModel.email.trim().length > 0);
const formModel = reactive({
  email: "",
  password: "",
});

// Modal 相關狀態
const showPrivacyModal = ref(false);
const showTermsModal = ref(false);
const privacyAgreed = ref(false);
const termsAgreed = ref(false);

// 離開確認 Modal 相關狀態
const showLeaveModal = ref(false);
const pendingNext = ref(null);
const shouldSkipLeaveConfirm = ref(false); // 用於標記是否跳過離開確認（例如註冊成功或無效邀請碼跳轉）

// 處理離開確認
const handleLeaveConfirm = () => {
  showLeaveModal.value = false;
  if (pendingNext.value) {
    pendingNext.value();
    pendingNext.value = null;
  }
};

const handleStay = () => {
  showLeaveModal.value = false;
  pendingNext.value = null;
};

// 路由守衛
onBeforeRouteLeave((to, from, next) => {
  if (shouldSkipLeaveConfirm.value) {
    next();
    return;
  }

  showLeaveModal.value = true;
  pendingNext.value = next;
});

// 瀏覽器關閉/重新整理守衛
const handleBeforeUnload = (e) => {
  if (!shouldSkipLeaveConfirm.value) {
    e.preventDefault();
    e.returnValue = "";
  }
};

onMounted(() => {
  window.addEventListener("beforeunload", handleBeforeUnload);
});

onUnmounted(() => {
  window.removeEventListener("beforeunload", handleBeforeUnload);
});

// 計算是否可以註冊（現在不需要檢查條款同意）
const canRegister = computed(() => {
  return true;
});

const isPasswordValid = computed(() => {
  if (formModel.password.length < 8) {
    return false;
  }
  const hasUppercase = /[A-Z]/.test(formModel.password);
  const hasLowercase = /[a-z]/.test(formModel.password);
  const hasNumbers = /[0-9]/.test(formModel.password);
  const hasSpecialChars = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(
    formModel.password
  );
  return hasUppercase && hasLowercase && hasNumbers && hasSpecialChars;
});

const handleNextStep = async () => {
  // 表單驗證
  let isValid = true;

  if (!formModel.email || !formModel.email.trim()) {
    message.error($t("register.emailRequired"));
    isValid = false;
  } else {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formModel.email)) {
      message.error($t("register.emailInvalid"));
      isValid = false;
    }
  }

  if (!formModel.password) {
    message.error($t("register.passwordRequired"));
    isValid = false;
  } else if (!isPasswordValid.value) {
    message.error($t("register.passwordInvalid"));
    isValid = false;
  }

  if (!isValid) {
    return;
  }

  loading.value = true;

  try {
    const response = await authAPI.register({
      name: formModel.email.split("@")[0] || formModel.email,
      email: formModel.email,
      password: formModel.password,
      password_confirmation: formModel.password,
      standard: "loinc",
    });

    if (response.status === 201) {
      sessionStorage.setItem("registerEmail", formModel.email);
      message.success($t("register.success"));
      shouldSkipLeaveConfirm.value = true; // 註冊成功，跳過確認
      router.push("/register-profile");
    } else {
      message.error(response.data?.message || $t("register.serverError"));
    }
  } catch (error) {
    const responseData = error?.response?.data;
    if (responseData) {
      let errorMsg = responseData.message;
      if (responseData.content?.errors) {
        const fieldErrors = [];
        for (const field in responseData.content.errors) {
          const messages = responseData.content.errors[field];
          if (Array.isArray(messages)) {
            fieldErrors.push(...messages);
          }
        }
        if (fieldErrors.length > 0) {
          errorMsg = fieldErrors.join(" ");
        }
      }
      message.error(errorMsg || $t("register.serverError"));
    } else {
      message.error($t("register.serverError"));
    }
  } finally {
    loading.value = false;
  }
};

// 處理隱私權政策同意
const handlePrivacyAgree = () => {
  privacyAgreed.value = true;
  showPrivacyModal.value = false;
};

// 處理隱私權政策取消
const handlePrivacyCancel = () => {
  showPrivacyModal.value = false;
};

// 處理使用者條款同意
const handleTermsAgree = () => {
  termsAgreed.value = true;
  showTermsModal.value = false;
};

// 處理使用者條款取消
const handleTermsCancel = () => {
  showTermsModal.value = false;
};
</script>
<style scoped>
.register {
  width: 100%;
  min-height: 100vh;
  /* ✅ 讓它至少滿版，但允許內容控制高度 */
  background-color: var(--color-primary-bg);
  background: linear-gradient(180deg, #f9f9f9, #a7dff1);
  display: inline-block;
  justify-content: center;
  align-items: flex-start;
  /* ✅ 不要置中，避免底部拉太長 */
  padding: 60px 16px 24px;
  /* ✅ 加入 bottom padding 讓內容不貼底 */
  box-sizing: border-box;
}

.register-form {
  width: 100%;
  max-width: 550px;
  border-radius: 20px;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 42px 32px;
  box-sizing: border-box;
  transition: 0.4s ease-in-out;
  margin: 0 auto;
}

.register-form-title {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.register-form-title h2 {
  font-size: 30px;
  font-weight: 700;
  color: #000000;
  margin: 0;
}

.register-form-title .subtitle {
  font-size: 18px;
  font-weight: 500;
  color: var(--color-primary);
  margin: 8px 0 0;
}

.register-form-title .description {
  font-size: 14px;
  font-weight: 400;
  color: var(--color-primary);
  margin: 8px 0 0;
  text-align: center;
  white-space: pre-wrap;
}

.register-panel {
  width: 100%;
  max-width: 472px;
  height: 100%;
  display: flex;
  flex-direction: column;
  margin-top: 40px;
  gap: 20px;
}

.login-email {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.login-email span {
  font-size: 16px;
  font-weight: 400;
  color: #414143;
}

.custom-form-item {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.phone-form-item {
  margin-top: 30px !important;
}

.form-label {
  font-size: 16px;
  font-weight: 500;
  color: #696969;
  margin-bottom: 0;
}

.required {
  color: #ff4d4f;
  margin-left: 2px;
}

.custom-input .ant-input {
  height: 64px;
  border-radius: 20px;
  padding: 0 16px;
  font-size: 16px;
}

.custom-input .ant-input:focus {
  border-color: #707070;
  box-shadow: none;
}

.create-with-email {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.create-w-email-btn {
  width: 100%;
  height: 64px;
  border-radius: 999px;
  background-color: var(--color-primary);
  color: #ffffff;
  font-size: 18px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
}

.create-w-email-btn:hover {
  background-color: var(--color-primary-hover);
  color: #ffffff;
}

.create-w-email-btn:active {
  background-color: var(--color-primary-active);
}

.create-w-email-btn:disabled {
  background-color: #d9d9d9 !important;
  color: #00000040 !important;
  border-color: #d9d9d9 !important;
  cursor: not-allowed;
}

.toc {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 500;
  color: #414143;
}

.toc-link {
  color: var(--color-primary-active);
  text-decoration: underline;
  font-weight: 500;
  padding: 10px 20px;
}

.toc-item {
  padding-right: 4px;
}

.exist-account {
  font-size: 16px;
  font-weight: 500;
  color: #414143;
  width: 100%;
  text-align: center;
}

.exist-account .toc-item {
  display: inline;
  padding-right: 4px;
}

.exist-account .toc-link {
  color: var(--color-primary-active);
  text-decoration: underline;
  font-weight: 500;
  text-align: center;
}

.custom-form-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  /* 錯誤訊息和 label 的間距 */
  width: 100%;
}

.custom-input :deep(.ant-form-item-row) {
  width: 100% !important;
}

.form-label {
  font-size: 16px;
  font-weight: 500;
  color: #000;
  margin-bottom: 0;
}

.required {
  color: #ff4d4f;
  margin-left: 2px;
  font-size: 16px;
}

.custom-input :deep(.ant-input),
.custom-input :deep(.ant-input-password) {
  height: 64px;
  border-radius: 20px;
  padding: 0 16px;
  font-size: 16px;
  color: #000;
  display: flex;
  align-items: center;
}

.custom-input :deep(.ant-input-password .ant-input) {
  border: none;
  box-shadow: none;
  height: 100%;
  padding: 0;
}

.custom-input :deep(.ant-input::placeholder),
.custom-input :deep(.ant-input-password .ant-input::placeholder) {
  color: #9c9c9c;
  opacity: 1;
}

.password-hint {
  margin-top: 8px;
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #696969;
  gap: 6px;
}

.hint-icon {
  font-size: 16px;
  color: #c0c0c0;
}

/* 🚫 移除寫死 height 的壞習慣，改用 min-height+flex */
.custom-input :deep(.ant-input),
.custom-input :deep(.ant-input-affix-wrapper),
.custom-input :deep(.ant-input-password),
.custom-input :deep(.ant-input-password .ant-input) {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
  border-radius: 20px !important;
  min-height: 56px !important;
  display: flex !important;
  align-items: center !important;
  padding: 0 16px !important;
  font-size: 15px !important;
  background-color: transparent !important;
  /* 👈 防止錯誤時多一層白底 */
}

/* 👀 補強錯誤狀態下的密碼欄位內部 input */
.custom-input :deep(.ant-input-password .ant-input) {
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
  height: 100% !important;
  background-color: transparent !important;
}

/* ✅ 修正 focus + 錯誤 狀態交疊會出現外框錯位 */
.custom-input :deep(.ant-input-affix-wrapper:focus),
.custom-input :deep(.ant-input-affix-wrapper-status-error),
.custom-input :deep(.ant-form-item-has-error .ant-input-affix-wrapper) {
  box-shadow: none !important;
  background-color: transparent !important;
}

/* ✅ 修正 form 錯誤顯示時 layout 跑位 */
.custom-input :deep(.ant-form-item-control-input),
.custom-input :deep(.ant-form-item-control-input-content) {
  width: 100% !important;
}

.terms-agreement {
  width: 100%;
  margin-bottom: 20px;
  text-align: center;
}

.terms-text {
  font-size: 14px;
  color: #696969;
  line-height: 1.5;
}

.terms-link {
  color: var(--color-primary);
  text-decoration: none;
  cursor: pointer;
  transition: color 0.3s;
}

.terms-link:hover {
  color: var(--color-primary-hover);
  text-decoration: underline;
}

.modal-content {
  max-height: 60vh;
  overflow-y: auto;
  padding: 0 10px;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px 0;
}

.error-container {
  text-align: center;
  padding: 30px 0;
}

.policy-content {
  line-height: 1.6;
  color: #333;
}

.policy-content p {
  margin-bottom: 16px;
}

/* ✅ RWD: 平板以下時自動縮小 */
@media (max-width: 1024px) {
  .register-form {
    padding: 32px 24px;
    height: auto !important;
    /* ✅ 不要固定高度 */
    max-height: 90vh !important;
    /* ✅ 最多佔滿 90% 螢幕高度 */
    overflow-y: auto;
    /* ✅ 如果內容太多可滾動 */
  }

  .register-panel {
    gap: 32px !important;
    margin-top: 40px !important;
  }

  .social-btn {
    height: 48px !important;
    font-size: 15px !important;
  }

  .create-w-email-btn {
    height: 48px !important;
    font-size: 16px !important;
    border-radius: 40px !important;
  }

  .custom-input :deep(.ant-input),
  .custom-input :deep(.ant-input-password) {
    height: 56px !important;
    font-size: 15px !important;
  }

  .custom-input :deep(.ant-select-selector) {
    height: 56px !important;
    font-size: 15px !important;
  }

  .register-form-title h2 {
    font-size: 26px !important;
  }

  .register-form-title p {
    font-size: 16px !important;
  }

  .login-devider span {
    font-size: 16px !important;
  }

  .password-hint {
    font-size: 13px !important;
  }

  .create-with-email {
    margin-top: 0;
  }

  .exist-account {
    display: inline-block;
    text-align: center;
    font-size: 14px;
    width: 100%;
  }

  .exist-account .toc-link {
    text-align: center;
  }
}

.project-sites-link {
  color: var(--color-primary, #3b42e2);
  text-decoration: underline;
  cursor: pointer;
  font-weight: 500;
}

.project-sites-link:hover {
  color: var(--color-primary-hover, #2c32c4);
}

.project-sites-card {
  position: relative;
  padding: 42px 32px;
}

.project-sites-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-bottom: 28px;
}

.back-btn {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  transition: background-color 0.2s ease;
}

.back-btn:hover {
  background-color: #f0f0f0;
}

.back-arrow-icon {
  font-size: 20px;
  color: #333;
}

.project-sites-title {
  font-size: 26px;
  font-weight: 700;
  color: #000;
  margin: 0;
  text-align: center;
}

.project-sites-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
}

.project-site-item {
  display: flex;
  flex-direction: column;
  text-decoration: none;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 14px;
  transition: opacity 0.2s ease;
}

.project-site-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.project-site-item:hover {
  opacity: 0.85;
}

.site-item-header {
  display: flex;
  align-items: center;
  gap: 6px;
}

.site-item-name {
  font-size: 18px;
  font-weight: 700;
  color: var(--color-primary, #3b42e2);
}

.site-item-arrow {
  font-size: 14px;
  color: var(--color-primary, #3b42e2);
}

.site-item-desc {
  font-size: 14px;
  color: #696969;
  margin: 4px 0 0 0;
  line-height: 1.5;
}
</style>
